import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Globe, Shield, Users } from 'lucide-react';
import { corporateIdentityAPI } from '../utils/api';
import CorporateIdentityBlock from '../components/CorporateIdentityBlock';
import CorporateCertificates from '../components/CorporateCertificates';
import SEO from '../components/SEO';

/**
 * AboutPage — canonical Corporate Identity & Trust surface.
 *
 * SSOT: `legal_entities` (identity) + `treatx_corporate_certificates`
 * (trust marks). Both come from the same canonical CMS as the Homepage —
 * no duplication of company data on the client. See
 * backend/routes/corporate_identity_routes.py.
 *
 * Slice 2 layout:
 *   1. Hero + mission
 *   2. Short corporate/trust SUMMARY (reads the same entity payload)
 *   3. Values grid
 *   4. Corporate certificates: granted + pending (canonical CMS)
 *   5. Stats
 *   6. Full Corporate Identity BLOCK (reads the same entity payload)
 */
const AboutPage = () => {
  const { t } = useTranslation();
  const [entity, setEntity] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [entRes, certRes] = await Promise.all([
          corporateIdentityAPI.getDefault().catch(() => null),
          corporateIdentityAPI.listCertificates({ surface: 'about' }).catch(() => null),
        ]);
        if (cancelled) return;
        if (entRes?.data) setEntity(entRes.data);
        const list = certRes?.data?.certificates;
        if (Array.isArray(list)) setCertificates(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const certLabels = {
    grantedHeading: t('corporateIdentity.certGrantedHeading', 'Certified & Authorized'),
    grantedCaption: t('corporateIdentity.certGrantedCaption', 'Certificates currently held by the company.'),
    pendingHeading: t('corporateIdentity.certPendingHeading', 'Authorizations in Progress'),
    pendingCaption: t('corporateIdentity.certPendingCaption', 'These authorizations have been applied for and are not yet granted.'),
    pending: t('corporateIdentity.pending', 'Application in progress'),
    clickToEnlarge: t('corporateIdentity.clickToEnlarge', 'Click to enlarge'),
    lightbox: {
      pending: t('corporateIdentity.pending', 'Application in progress'),
      openPdf: t('corporateIdentity.openPdf', 'Open PDF'),
      pdfNotice: t('corporateIdentity.pdfNotice', 'This certificate is only available as a PDF document.'),
      noAsset: t('corporateIdentity.noAsset', 'A copy of this certificate has not been published yet.'),
      noAssetPending: t('corporateIdentity.noAssetPending', 'The certificate document will be published here once the authorization is granted.'),
      certNumber: t('corporateIdentity.certNumber', 'Certificate no.'),
      scope: t('corporateIdentity.scope', 'Scope'),
      issueDate: t('corporateIdentity.issueDate', 'Issued'),
      validUntil: t('corporateIdentity.validUntil', 'Valid until'),
    },
  };

  const idLabels = {
    summaryEyebrow: t('corporateIdentity.summaryEyebrow', 'A registered Türkiye health-tourism company'),
    blockHeading: t('corporateIdentity.blockHeading', 'Corporate Identity'),
    blockSubheading: t('corporateIdentity.blockSubheading', 'Public registration details of the entity operating this platform.'),
    commercialTitle: t('corporateIdentity.commercialTitle', 'Commercial title'),
    brand: t('corporateIdentity.brand', 'Brand'),
    mersis: t('corporateIdentity.mersis', 'MERSİS number'),
    tradeRegistry: t('corporateIdentity.tradeRegistry', 'Trade registry no.'),
    taxOffice: t('corporateIdentity.taxOffice', 'Tax office'),
    taxId: t('corporateIdentity.taxId', 'Tax ID'),
    chamber: t('corporateIdentity.chamber', 'Chamber'),
    chamberRegistry: t('corporateIdentity.chamberRegistry', 'Chamber registry no.'),
    registeredAddress: t('corporateIdentity.registeredAddress', 'Registered address'),
    generalEmail: t('corporateIdentity.generalEmail', 'General enquiries'),
    kvkkEmail: t('corporateIdentity.kvkkEmail', 'Data protection (KVKK) contact'),
    kep: t('corporateIdentity.kep', 'Registered Electronic Mail (KEP)'),
    phone: t('corporateIdentity.phone', 'Phone'),
    applicationInProgress: t('corporateIdentity.pending', 'Application in progress'),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-primary/5" data-testid="about-page">
      <SEO
        title={t('aboutPage.metaTitle', 'About TreatX — Verified Medical Tourism')}
        description={t(
          'aboutPage.metaDescription',
          'Learn how TreatX connects patients with verified medical providers worldwide. Our corporate identity, certifications and authorizations are backed by canonical trust records.'
        )}
        canonicalPath="/about"
        jsonLd={
          entity
            ? {
                '@context': 'https://schema.org',
                '@type': 'AboutPage',
                url: 'https://treatx.health/about',
                mainEntity: {
                  '@type': 'Organization',
                  name: entity.commercial_title || entity.brand || 'TreatX',
                  brand: entity.brand || 'TreatX',
                  taxID: entity.tax_id || undefined,
                  address: entity.registered_address || undefined,
                  email: entity.general_email || undefined,
                },
              }
            : undefined
        }
      />
      <div className="container-custom section">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-4">
            {t('aboutPage.title', 'About TreatX')}
          </h1>
          <p className="text-foreground/70 text-lg max-w-3xl mx-auto">
            {t('aboutPage.subtitle', 'Connecting patients with world-class medical providers through innovative technology')}
          </p>
        </div>

        {/* Corporate summary (canonical CMS) */}
        {entity && (
          <div className="max-w-4xl mx-auto mb-12">
            <CorporateIdentityBlock entity={entity} labels={idLabels} variant="summary" />
          </div>
        )}

        {/* Mission */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-12 border border-slate-200">
          <h2 className="text-3xl font-bold text-secondary mb-6 text-center">
            {t('aboutPage.missionTitle', 'Our Mission')}
          </h2>
          <p className="text-foreground/70 text-lg leading-relaxed text-center max-w-4xl mx-auto">
            {t('aboutPage.missionBody', "At TreatX, we believe everyone deserves access to quality healthcare. We're building a global platform that makes medical tourism transparent, safe, and accessible. Through AI-powered matching and rigorous clinic verification, we help patients find the perfect medical provider for their needs.")}
          </p>
        </div>

        {/* Values */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="text-center" data-testid="value-trust">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mx-auto mb-4">
              <Shield className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-secondary mb-2">{t('aboutPage.valueTrustTitle', 'Trust')}</h3>
            <p className="text-foreground/70">{t('aboutPage.valueTrustBody', 'All clinics are thoroughly verified and certified')}</p>
          </div>

          <div className="text-center" data-testid="value-transparency">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mx-auto mb-4">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-secondary mb-2">{t('aboutPage.valueTransparencyTitle', 'Transparency')}</h3>
            <p className="text-foreground/70">{t('aboutPage.valueTransparencyBody', 'Clear pricing and honest reviews')}</p>
          </div>

          <div className="text-center" data-testid="value-global">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mx-auto mb-4">
              <Globe className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-secondary mb-2">{t('aboutPage.valueGlobalTitle', 'Global Reach')}</h3>
            <p className="text-foreground/70">{t('aboutPage.valueGlobalBody', 'Cross-border access to vetted medical providers')}</p>
          </div>

          <div className="text-center" data-testid="value-support">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mx-auto mb-4">
              <Users className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-secondary mb-2">{t('aboutPage.valueSupportTitle', 'Support')}</h3>
            <p className="text-foreground/70">{t('aboutPage.valueSupportBody', '24/7 assistance throughout your journey')}</p>
          </div>
        </div>

        {/* Canonical Corporate Certificates */}
        {certificates.length > 0 && (
          <div className="mb-16" data-testid="about-corporate-certificates">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-secondary mb-2">
                {t('corporateIdentity.aboutCertHeading', 'Certifications & Authorizations')}
              </h2>
              <p className="text-sm text-slate-600 max-w-2xl mx-auto">
                {t('corporateIdentity.aboutCertSubheading', 'The status of every certificate and authorization is shown truthfully — granted items appear separately from applications still in progress.')}
              </p>
            </div>
            <CorporateCertificates certificates={certificates} labels={certLabels} variant="about" />
          </div>
        )}

        {/* Issue 3 (Owner UAT): the "TreatX by the Numbers" tile
            previously rendered hard-coded marketing numbers (500+ /
            50+ / 10k+). Until the canonical `/api/public/platform-stats`
            authority carries real values, this block would publish
            fabricated metrics — Owner directive forbids that. The block
            is intentionally removed from About; the Homepage still
            renders the same section but backed by canonical
            `platformStats` (real data) with graceful empty state. */}

        {/* Full corporate identity block (canonical CMS) */}
        {entity && (
          <div className="max-w-4xl mx-auto">
            <CorporateIdentityBlock entity={entity} labels={idLabels} variant="full" />
          </div>
        )}

        {loading && !entity && (
          <div className="text-center text-sm text-slate-500 py-8" data-testid="about-loading">
            {t('common.loading', 'Loading…')}
          </div>
        )}
      </div>
    </div>
  );
};

export default AboutPage;
